import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useLogin } from '../api/hooks';
import { Button, TextInput } from '../components/ui';
import { getInitData, getTg } from '../lib/tg';
import { useT } from '../i18n';
import { BUILD_VERSION } from '../lib/buildInfo';

const TOS_URL = 'https://neburst.com/legal/terms-of-service/';

export function Login() {
  const navigate = useNavigate();
  const login = useLogin();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const openTos = () => {
    const tg = getTg();
    if (tg?.openLink) tg.openLink(TOS_URL);
    else window.open(TOS_URL, '_blank', 'noopener,noreferrer');
  };

  const submit = () => {
    setErr(null);
    login.mutate(
      { email, password },
      {
        onSuccess: (data) => {
          if (data.requires2fa) {
            getTg()?.HapticFeedback.notificationOccurred('warning');
            navigate('/totp');
            return;
          }
          if (data.ok) {
            getTg()?.HapticFeedback.notificationOccurred('success');
            return;
          }
          setErr(data.error ?? t('login.failed'));
        },
        onError: (e) => {
          setErr(String(e));
          getTg()?.HapticFeedback.notificationOccurred('error');
        },
      },
    );
  };

  return (
    <div
      className="fade-in"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--tg-bg)',
        padding: 'calc(var(--safe-top) + 24px) 24px 32px',
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginBottom: 12,
          }}
        >
          <img
            src="/favicon.png"
            alt="Neburst"
            width={44}
            height={44}
            style={{
              display: 'block',
              filter: 'drop-shadow(0 6px 14px rgba(7, 141, 238, 0.2))',
            }}
          />
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              color: 'var(--tg-text)',
            }}
          >
            {t('login.title')}
          </h1>
        </div>
        <p
          style={{
            margin: '0 0 32px',
            color: 'var(--tg-subtitle)',
            fontSize: 15,
            lineHeight: 1.45,
            maxWidth: 320,
          }}
        >
          {t('login.subtitle')}
        </p>

        <div style={{ display: 'grid', gap: 12 }}>
          <TextInput
            value={email}
            onChange={setEmail}
            placeholder={t('login.emailPlaceholder')}
            type="email"
            inputMode="email"
          />
          <TextInput
            value={password}
            onChange={setPassword}
            placeholder={t('login.passwordPlaceholder')}
            type={show ? 'text' : 'password'}
            trailing={
              <button
                onClick={() => setShow((s) => !s)}
                style={{ color: 'var(--tg-hint)', display: 'inline-flex' }}
                aria-label="toggle"
              >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
          />

          {err && (
            <div
              style={{
                color: 'var(--danger)',
                fontSize: 13,
                padding: '4px 2px',
              }}
            >
              {err}
            </div>
          )}

          <div style={{ marginTop: 6 }}>
            <Button
              onClick={submit}
              disabled={email.length === 0 || password.length === 0 || login.isPending}
              loading={login.isPending}
              leading={!login.isPending && <LogIn size={16} />}
              size="lg"
            >
              {t('login.submit')}
            </Button>
          </div>
        </div>
      </div>

      <p
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--tg-hint)',
          margin: 0,
          paddingBottom: 'var(--safe-bottom)',
        }}
      >
        {t('login.footer.agree')}{' '}
        <button
          onClick={openTos}
          style={{
            color: 'var(--brand)',
            fontWeight: 500,
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          {t('login.footer.tos')}
        </button>
      </p>
      <p
        style={{
          textAlign: 'center',
          fontSize: 10,
          color: 'var(--tg-hint)',
          opacity: 0.5,
          margin: '6px 0 0',
        }}
      >
        build {BUILD_VERSION} · initData {getInitData().length}b{' '}
        {window.Telegram?.WebApp?.platform ? `· ${window.Telegram.WebApp.platform}` : ''}
      </p>
    </div>
  );
}
